import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';

import { AuthenticationError, NavinaError } from './components/ErrorPage/Errors';
import {
	SomethingWentWrong,
	type SomethingWentWrongProps,
} from './components/ErrorPage/SomethingWentWrong/SomethingWentWrong';
import { getOrCreateInitializedLogRocketModel } from './components/LogRocket/LogRocketModel';
import { IS_PRODUCTION } from './config/Environment';
import { getOrCreateApiTraceServer } from './server/apiTrace';
import { theme } from './theme/theme';
import { reloadPage } from './utilities/location';
import { getOrCreateAnalytics } from './utils/analytics';
import { getOrCreateLogger } from './utils/logger';

const ErrorDetailsContainer = styled.code`
	display: block;
	block-size: 300px;
	overflow-y: auto;
	background-color: whitesmoke;
	outline: 1px solid black;
	text-align: start;
`;

const ErrorUserAgent = styled.code`
	font-size: ${theme.font.sizes.xs};
	color: ${theme.font.colors.secondary};
`;

// Do we still need it? Consider refactoring / removing
const logErrorToMyService = (error: Error, info: unknown): void => {
	if (IS_PRODUCTION) {
		getOrCreateApiTraceServer()
			.logError('error_in_frontend', error, info)
			.catch(function logToConsole(loggingError): void {
				console.log('error logging the event', loggingError);
			});

		getOrCreateLogger().error('Error boundary', { type: 'error_in_frontend', error, info });
		console.log('Error boundary', { type: 'error_in_frontend', error, info });

		getOrCreateAnalytics().track(getOrCreateAnalytics().idsNames.Error, { error, info });
	}
};

export function ErrorBoundary() {
	const [shouldShowError, setShouldShowError] = useState<boolean>(false);
	const error = useRouteError();

	useEffect(
		function reloadPageOnFailingDynamicallyModuleImporting(): void {
			if (!(error instanceof globalThis.Error)) {
				return;
			}

			const { message, name } = error;
			if (message.startsWith('Failed to fetch dynamically imported module:') && name === 'TypeError') {
				reloadPage({ preventLoop: true });
			}
		},
		[error],
	);

	useEffect(function informLogRocket(): void {
		if (!(error instanceof Error)) {
			// @ts-expect-error - Legacy code, nullability not yet enforced
			return null;
		}

		console.error(error);

		getOrCreateInitializedLogRocketModel().captureException(error);

		logErrorToMyService(error, { info: null });
	}, []);

	if (!(error instanceof Error)) {
		return null;
	}

	const revealErrorMessage = (): void => {
		setShouldShowError(true);
	};

	const hidableErrorDetails = shouldShowError && (
		<details>
			<summary>{error ? <code>{error.message}</code> : 'Additional details'}</summary>
			{error && <ErrorDetailsContainer>{JSON.stringify(error.stack)}</ErrorDetailsContainer>}
			<ErrorUserAgent>{navigator.userAgent}</ErrorUserAgent>
		</details>
	);

	const somethingWentWrongProps = {
		onDoubleClick: revealErrorMessage,
		action: {
			name: 'Reload page',
			onAction: reloadPage,
		},
		children: hidableErrorDetails,
	} as const satisfies SomethingWentWrongProps;

	if (error instanceof AuthenticationError) {
		const { isHiddenError } = error;

		if (isHiddenError) {
			return <div>...</div>;
		}
	}

	if (error instanceof NavinaError) {
		const { message: errorTitle, description: errorMessage } = error;

		return (
			<SomethingWentWrong
				{...{
					...somethingWentWrongProps,
					errorMessage,
					errorTitle,
				}}
			/>
		);
	}

	if (isRouteErrorResponse(error)) {
		return (
			<SomethingWentWrong
				{...{
					...somethingWentWrongProps,
					errorMessage: error.data,
					errorTitle: `Route error (${error.status} ${error.statusText})`,
				}}
			/>
		);
	}

	const { message: errorMessage, name: errorTitle } = error;
	return (
		<SomethingWentWrong
			{...{
				...somethingWentWrongProps,
				errorMessage,
				errorTitle,
			}}
		/>
	);
}
